import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, Typography } from '@mui/material';
import { getSectionColor } from './styles/theme';

const CardContainer = styled(Box, {
  shouldForwardProp: (prop) => !['isSelected', 'sectionType', 'sectionColor'].includes(prop),
})(({ theme, isSelected, sectionColor }) => {
  const primaryColor = sectionColor?.primary || '#996CFE';
  const rgbaColor = sectionColor ? 
    `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.15)` :
    'rgba(153, 108, 254, 0.15)';
  const rgbaHoverColor = sectionColor ? 
    `rgba(${parseInt(primaryColor.slice(1, 3), 16)}, ${parseInt(primaryColor.slice(3, 5), 16)}, ${parseInt(primaryColor.slice(5, 7), 16)}, 0.2)` :
    'rgba(153, 108, 254, 0.2)';
  
  return {
    borderRadius: '12px',
    padding: '12px',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: isSelected ? sectionColor?.lighter || '#F8F2FF' : '#FFFFFF',
    border: isSelected ? `2px solid ${primaryColor}` : '1px solid #E2E8F0',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '90px',
    position: 'relative',
    boxShadow: isSelected ? `0 4px 12px ${rgbaColor}` : '0 1px 3px rgba(0,0,0,0.02)',
    boxSizing: 'border-box',
    overflow: 'visible',
    '&:hover': {
      borderColor: primaryColor,
      transform: 'translateY(-2px)',
      boxShadow: isSelected ? `0 6px 15px ${rgbaHoverColor}` : '0 4px 10px rgba(0, 0, 0, 0.05)',
    },
    ...(isSelected && {
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-12px',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '10px solid transparent',
        borderRight: '10px solid transparent',
        borderTop: `12px solid ${primaryColor}`,
        zIndex: 2,
      },
      '&::before': {
        content: '""',
        position: 'absolute',
        bottom: '-13px',
        left: '50%',
        transform: 'translateX(-50%)',
        borderLeft: '11px solid transparent',
        borderRight: '11px solid transparent',
        borderTop: '13px solid transparent',
        zIndex: 1,
      }
    }),
    [theme.breakpoints.down('sm')]: {
      height: '80px',
      padding: '8px',
    },
  };
});

const IconContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'iconColor',
})(({ iconColor }) => ({
  width: '32px',
  height: '32px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: '6px',
  '& svg': {
    '& path': {
      fill: iconColor || 'currentColor',
    },
  },
}));

const SectionTitle = styled(Typography)({
  fontSize: '14px',
  fontWeight: 700,
  color: '#2D3748',
  marginBottom: '2px',
  textAlign: 'center',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const TestCount = styled(Typography)({
  fontSize: '12px',
  fontWeight: 600,
  color: '#718096',
  textAlign: 'center',
  fontFamily: "'Poppins', 'Inter', sans-serif",
});

const SectionCard = ({ title, count, icon, onClick, isSelected }) => {
  const sectionColor = getSectionColor(title);
  
  return (
    <CardContainer 
      onClick={onClick} 
      sectionType={title}
      isSelected={isSelected}
      sectionColor={sectionColor}
    >
      <IconContainer iconColor={sectionColor.primary}>
        {icon}
      </IconContainer>
      <SectionTitle>{title}</SectionTitle>
      <TestCount>{count} tests</TestCount>
    </CardContainer>
  );
};

export default SectionCard; 